import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ADD_TEST, TESTS_API } from "../../constant";
import { DataGrid } from "../index";
import { start, done } from "../../store/loaderSlice";
import { convertTimestamp } from "../../utils/convertTimestamp";
import { snackbarStart } from "../../store/SnackbarSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { renderHTMLCell } from "../../utils/renderHTMLCell";
import { CSVLink } from "react-csv";
import TestForm from "./TestForm";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const DashboardTests = () => {
  const { token } = useSelector((state) => state.user);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [tests, setTests] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editTestId, setEditTestId] = useState(null);

  const fetchTests = async () => {
    dispatch(start());
    try {
      const { data } = await axios.get(ADD_TEST, {
        headers: { "x-auth-token": token },
      });
      setTests(data);
    } catch (err) {
      console.error(err);
      dispatch(done());
      navigate("/");
    } finally {
      dispatch(done());
    }
  };

  const handleDeleteTests = async () => {
    if (!selectedIds.length) return;
    dispatch(start());
    try {
      await axios.delete(TESTS_API, {
        headers: { "Content-Type": "application/json", "x-auth-token": token },
        data: { ids: selectedIds },
      });
      dispatch(
        snackbarStart({
          text: t("testsDashboard.deletedMessage"),
          severity: "success",
        }),
      );
      handleCancel();
      fetchTests();
    } catch {
      dispatch(
        snackbarStart({ text: t("somethingWentWrong"), severity: "error" }),
      );
    } finally {
      dispatch(done());
    }
  };

  const handleAddTest = () => {
    setEditTestId(null);
    setShowForm(true);
  };

  const handleEditTest = () => {
    if (selectedIds.length !== 1) {
      dispatch(
        snackbarStart({
          text: t("selectOneItem"),
          severity: "warning",
        }),
      );
      return;
    }
    setEditTestId(selectedIds[0]);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedIds([]);
    setEditTestId(null);
  };

  const onSelectionChange = (ids) => setSelectedIds(ids);

  const columns = [
    { field: "id", headerName: "ID", width: 220 },
    { field: "title", headerName: t("testsDashboard.name"), width: 200 },
    { field: "updatedAt", headerName: t("testsDashboard.date"), width: 180 },
    {
      field: "description",
      headerName: t("testsDashboard.desc"),
      width: 200,
      renderCell: (params) => renderHTMLCell(params.value),
    },
    { field: "isPublic", headerName: t("testsDashboard.isPublic"), width: 90 },
    {
      field: "questionCount",
      headerName: t("testsDashboard.numTest"),
      width: 120,
    },
  ];

  const rows = tests.map((test) => ({
    id: test._id,
    title: test.title,
    updatedAt: convertTimestamp(test.updatedAt),
    description: test.description,
    isPublic: test.isPublic,
    questionCount: test.questions?.length || 0,
  }));

  const csvHeaders = columns.map(({ headerName, field }) => ({
    label: headerName,
    key: field,
  }));

  useEffect(() => {
    if (token) fetchTests();
  }, [token]);

  return (
    <section className="min-h-screen pt-24 pb-16 bg-gray-50 dark:bg-[#030712] transition-colors duration-300">
      <div className="container mx-auto px-4">
        {/* Page header */}
        <div className="flex items-center gap-4 mb-8 mt-2">
          <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center text-green-600 dark:text-green-400 flex-shrink-0">
            <AssignmentIcon sx={{ fontSize: 26 }} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">
              {t("dashboard.tests")}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t("testsDashboard.totalQuestions")}: {tests.length}
            </p>
          </div>
        </div>

        {/* Action toolbar */}
        <div className="flex flex-wrap gap-2.5 mb-5">
          <button
            onClick={handleAddTest}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <AddIcon sx={{ fontSize: 17 }} /> {t("testsDashboard.newTest")}
          </button>

          <button
            onClick={handleEditTest}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <EditIcon sx={{ fontSize: 17 }} />
            {t("edit")}
          </button>

          <button
            onClick={handleDeleteTests}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <DeleteIcon sx={{ fontSize: 17 }} /> {t("delete")}
          </button>

          <CSVLink
            data={rows}
            headers={csvHeaders}
            filename="tests.csv"
            className="no-underline"
          >
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-white text-sm font-semibold transition-all">
              <FileDownloadIcon sx={{ fontSize: 17 }} /> {t("exportToCsv")}
            </button>
          </CSVLink>
        </div>

        {/* Data grid */}
        <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-[#0f172a] shadow-sm">
          <DataGrid
            rows={rows}
            columns={columns}
            title=""
            checkboxSelection
            selectedIds={selectedIds}
            sx={{
              border: "none",
              "& .MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
            }}
            onRowsSelectionHandler={onSelectionChange}
          />
        </div>

        {/* Test form modal */}
        {showForm && (
          <TestForm
            setFormStatus={setShowForm}
            testId={editTestId}
            refreshData={fetchTests}
            setSelectedIds={setSelectedIds}
          />
        )}
      </div>
    </section>
  );
};

export default DashboardTests;
